import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  BASE_URL_FOR_ELASTIC_SEARCH,
  SHOP_BY_BRAND_PRODUCTS,
  SHOP_BY_BRAND_PRODUCTS_DB,
  USING_SANDBOX_ENVIRONMENT,
} from '../../../Constants/Config';
import { waitForElasticConnection } from '../../../hooks/checkElasticConnection.js';
import { createRequestCache } from '../../../utils/requestCache';

const brandProductsRequestCache = createRequestCache({
  ttlMs: 45 * 1000,
  maxEntries: 60,
});

const initialState = {
  loading: false,
  brand: null,
  products: {},
  activeRequestId: null,
  error: '',
};

// AsyncThunk for fetching brand products
export const fetchBrandProducts = createAsyncThunk(
  'brandDetail/fetchBrandProducts',
  async (data, { getState, signal, rejectWithValue }) => {
    const normalizedStoreType =
      data?.store === 'delivery' || data?.store === 'pickup' ? data.store : '';
    const requestLat = data?.location?.lat ?? '';
    const requestLong = data?.location?.lng ?? '';
    const requestKey = JSON.stringify([
      BASE_URL_FOR_ELASTIC_SEARCH,
      BASE_URL_SANDBOX,
      SHOP_BY_BRAND_PRODUCTS,
      SHOP_BY_BRAND_PRODUCTS_DB,
      String(data?.title || ''),
      String(requestLat),
      String(requestLong),
      normalizedStoreType,
      String(USING_SANDBOX_ENVIRONMENT),
    ]);

    try {
      return await brandProductsRequestCache.getOrCreate(
        requestKey,
        async () => {
          const isElasticConnected = await waitForElasticConnection(getState);
          const baseUrl = isElasticConnected
            ? BASE_URL_FOR_ELASTIC_SEARCH
            : BASE_URL_SANDBOX;
          const endpoint = isElasticConnected
            ? SHOP_BY_BRAND_PRODUCTS
            : SHOP_BY_BRAND_PRODUCTS_DB;
          const formData = new FormData();
          formData.append('smartsearch', data?.title || '');
          formData.append('lat', requestLat);
          formData.append('long', requestLong);
          formData.append('status', USING_SANDBOX_ENVIRONMENT);
          if (normalizedStoreType) {
            formData.append('store', normalizedStoreType);
          }

          const response = await axios.post(
            `${baseUrl}${endpoint}`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          );

          if (response.data.status !== true) {
            throw new Error('Failed to fetch brand products');
          }

          return {
            brand: data?.title || '',
            products: response?.data?.data || {},
          };
        },
        {
          forceRefresh: Boolean(data?.forceRefresh),
          signal,
        },
      );
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return rejectWithValue('Request cancelled');
      }
      return rejectWithValue(error.message);
    }
  }
);

const brandDetailSlice = createSlice({
  name: 'brandDetail',
  initialState,
  reducers: {
    resetBrandDetail: (state) => {
      state.loading = false;
      state.brand = null;
      state.products = {};
      state.activeRequestId = null;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrandProducts.pending, (state, action) => {
        state.loading = true;
        state.activeRequestId = action.meta.requestId;
        state.brand = null;
        state.products = {};
        state.error = '';
      })
      .addCase(fetchBrandProducts.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestId = null;
        state.brand = action.payload.brand;
        state.products = action.payload.products;
      })
      .addCase(fetchBrandProducts.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestId = null;
        if (!action.meta.aborted) {
          state.error = action.payload || 'Failed to fetch brand products';
        }
      });
  },
});

export const { resetBrandDetail } = brandDetailSlice.actions;
export default brandDetailSlice.reducer;
